import React, { useState, useEffect, useContext } from "react";
import Paper from "@material-ui/core/Paper";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Avatar from "@material-ui/core/Avatar";

import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SendIcon from "@material-ui/icons/Send";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import HistoryIcon from "@material-ui/icons/History";
import BugReportIcon from "@material-ui/icons/BugReport";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { parseISO, format } from "date-fns";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  kpiCard: {
    padding: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: 16,
    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.04)",
    color: "#fff",
    transition: "transform 0.2s ease-in-out",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 24px 0 rgba(0,0,0,0.08)",
    },
  },
  kpiIconWrapper: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
  },
  kpiIcon: {
    fontSize: "2rem",
    color: "#fff",
  },
  kpiTitle: {
    fontWeight: 600,
    fontSize: "0.9rem",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    opacity: 0.9,
  },
  kpiValue: {
    fontWeight: 700,
    fontSize: "2.2rem",
    lineHeight: 1.2,
  },
  sectionPaper: {
    padding: theme.spacing(3),
    borderRadius: 16,
    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.04)",
    height: "100%",
    backgroundColor: theme.palette.background.paper,
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: "1.2rem",
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: "10px"
  },
  logItem: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
      marginBottom: 0,
      paddingBottom: 0,
      borderBottom: "none",
    }
  },
  logAvatar: {
    width: 32,
    height: 32,
    marginRight: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    fontSize: "0.8rem",
    fontWeight: "bold",
  },
  logAction: {
    fontWeight: "bold",
    fontSize: "0.85rem",
    color: theme.palette.text.primary,
  },
  logDate: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  chartPlaceholder: {
    height: 300,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
    borderRadius: 12,
    border: `2px dashed ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
  }
}));

const ModernKpiCard = ({ title, value, icon, gradient }) => {
  const classes = useStyles();
  return (
    <Paper className={classes.kpiCard} style={{ background: gradient }}>
      <Box>
        <Typography className={classes.kpiTitle}>{title}</Typography>
        <Typography className={classes.kpiValue}>{value}</Typography>
      </Box>
      <Box className={classes.kpiIconWrapper}>
        {React.cloneElement(icon, { className: classes.kpiIcon })}
      </Box>
    </Paper>
  );
};

const actionTranslations = {
  CREATE: "Creó",
  UPDATE: "Actualizó",
  DELETE: "Eliminó",
  LOGIN: "Inició sesión",
  LOGOUT: "Cerró sesión"
};

const Dashboard = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [data, setData] = useState({
    totalContacts: 0,
    activeConnections: 0,
    openTickets: 0,
    pendingTickets: 0,
    closedTickets: 0,
    totalCampaigns: 0,
    recentLogs: []
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get("/dashboard");
        setData(res.data);
      } catch (err) {
        console.error("Error fetching dashboard data", err);
      }
    };
    fetchData();
  }, []);

  return (
    <Container maxWidth="lg" className={classes.container}>
      <Grid container spacing={3}>
        {/* FILA 1: KPIs Nivel Dios */}
        <Grid item xs={12} sm={6} md={3}>
          <ModernKpiCard
            title="Conexiones Activas"
            value={data.activeConnections}
            icon={<SyncAltIcon />}
            gradient="linear-gradient(135deg, #00D4AA 0%, #009688 100%)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ModernKpiCard
            title="Contactos Totales"
            value={data.totalContacts}
            icon={<PeopleAltIcon />}
            gradient="linear-gradient(135deg, #4FC3F7 0%, #2196F3 100%)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ModernKpiCard
            title="Campañas Activas"
            value={data.totalCampaigns}
            icon={<SendIcon />}
            gradient="linear-gradient(135deg, #BA68C8 0%, #9C27B0 100%)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ModernKpiCard
            title="Tickets Resueltos"
            value={data.closedTickets}
            icon={<AssignmentTurnedInIcon />}
            gradient="linear-gradient(135deg, #FFB74D 0%, #FF9800 100%)"
          />
        </Grid>

        {/* FILA 2: Gráficas y Auditoría */}
        <Grid item xs={12} md={8}>
          <Paper className={classes.sectionPaper}>
            <Typography className={classes.sectionTitle}>
              <BugReportIcon style={{ color: "#00D4AA" }} />
              Estado de Tickets en Tiempo Real
            </Typography>
            <div className={classes.chartPlaceholder}>
              <Typography variant="h6" color="textSecondary">
                Resumen de Atención
              </Typography>
              <Box mt={2} display="flex" gap="20px">
                <Box textAlign="center">
                  <Typography variant="h3" style={{ color: "#F59E0B", fontWeight: "bold" }}>{data.pendingTickets}</Typography>
                  <Typography variant="caption">EN ESPERA</Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h3" style={{ color: "#3B82F6", fontWeight: "bold" }}>{data.openTickets}</Typography>
                  <Typography variant="caption">ATENDIENDO</Typography>
                </Box>
              </Box>
            </div>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper className={classes.sectionPaper}>
            <Typography className={classes.sectionTitle}>
              <HistoryIcon style={{ color: "#BCAAA4" }} />
              Auditoría Reciente
            </Typography>
            <Box mt={2}>
              {data.recentLogs && data.recentLogs.length > 0 ? (
                data.recentLogs.map((log) => (
                  <div key={log.id} className={classes.logItem}>
                    <Avatar className={classes.logAvatar}>
                      {log.user ? log.user.name.charAt(0).toUpperCase() : "S"}
                    </Avatar>
                    <Box>
                      <Typography className={classes.logAction}>
                        {log.user ? log.user.name : "Sistema"} ({actionTranslations[log.action?.toUpperCase()] || log.action})
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Módulo: {log.module} (ID: {log.recordId})
                      </Typography>
                      <Typography className={classes.logDate}>
                        {format(parseISO(log.createdAt), "dd/MM/yyyy HH:mm")}
                      </Typography>
                    </Box>
                  </div>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No hay registros recientes.
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;